import { useTranslation } from 'react-i18next';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { color, EL_COLORS } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { FlexCol, FlexCenVer } from '@/UI/styles';
import { EllipsisText } from '@/UI/EllipsisText/EllipsisText';
import { NodeTextField } from '@/UI/NodeTextField/NodeTextField';
import { I18N_KEYS } from '@/language/keys';
import type { TextAsset } from '@/types/api/assets';

interface TextViewerProps {
  file: TextAsset;
  index: number;
  onTextChange?: (index: number, value: string) => void;
  hasMultipleFiles?: boolean;
  isMultiFilesViewMode: boolean;
  fixedItemSize?: number;
  disableTooltip?: boolean;
}

export function TextViewer({
  file,
  index,
  onTextChange,
  hasMultipleFiles,
  isMultiFilesViewMode,
  fixedItemSize,
  disableTooltip = false,
}: TextViewerProps) {
  const { t } = useTranslation();
  const isFpl = useFplMigrationFlag();

  return (
    <FlexCol
      sx={{
        bgcolor: isFpl ? 'transparent' : color.Black84,
        width: '100%',
        position: 'relative',
        flex: 1,
        minHeight: 0,
      }}
      className="nodrag node-text-viewer"
    >
      {isMultiFilesViewMode ? (
        <FlexCenVer
          sx={{
            bgcolor: isFpl ? token.bgSecondary : color.Black84,
            width: '100%',
            height: `${fixedItemSize!}px`,
            px: 3,
            borderRadius: 2,
            border: `1px solid ${isFpl ? token.border : EL_COLORS.BoxBorder}`,
            justifyContent: 'flex-start',
          }}
        >
          <EllipsisText
            variant="body-std-rg"
            isMultiLine
            delay={500}
            enterNextDelay={800}
            disableHoverListener={disableTooltip}
            linesNumber={fixedItemSize! > 200 ? 8 : 4}
            sx={{
              color: token.textSecondary,
              width: '100%',
              fontSize: '16px',
              wordBreak: 'break-word',
            }}
          >
            {'value' in file ? file.value : ''}
          </EllipsisText>
        </FlexCenVer>
      ) : (
        <NodeTextField
          aria-label={t(I18N_KEYS.RECIPE_MAIN.NODES.TEXT_VIEWER.ARIA_LABEL)}
          value={'value' in file ? file.value : ''}
          multiline
          fullWidth
          size="small"
          onChange={(e) => {
            if (onTextChange) {
              onTextChange(index, e.target.value);
            }
          }}
          readOnly={!onTextChange}
          rootSx={{
            transition: 'padding 0.1s ease-in-out',
            p: 3,
            pt: hasMultipleFiles ? 5 : 4,
            pb: 4,
            ...(!onTextChange && { cursor: 'inherit', '& textarea': { cursor: 'inherit' } }),
          }}
        />
      )}
    </FlexCol>
  );
}
